<template>
    <page-controls>
        <template v-slot:left>
            <div class="grow @3xl/controls:max-w-md">
                <vx-filterable :active="active" @toggle="toggleFilterable" data-test="filters">
                    <template v-slot:placeholder>
                        <span class="mr-2">Find</span>
                        <vx-badge
                            v-if="filtersCount > 0"
                            data-test="reset"
                            :borders="false"
                            size="xs"
                            @click.stop="reset"
                        >
                            {{ pluralized('filter', filtersCount, true) }}
                            <font-awesome-icon widthAuto :icon="faCircleXmark" />
                        </vx-badge>
                    </template>
                    <template v-slot:form>
                        <form @submit.prevent="apply" class="flex flex-col gap-4">
                            <div>
                                <filter-label :canReset="!!filters.query" @reset="filters.query = undefined">
                                    Keyword Search
                                </filter-label>
                                <vx-input
                                    type="text"
                                    data-test="query"
                                    placeholder="Search"
                                    maxlength="80"
                                    size="lg"
                                    v-model="filters.query"
                                >
                                    <template v-slot:right>
                                        <font-awesome-icon widthAuto class="text-xs" :icon="faMagnifyingGlass" />
                                    </template>
                                </vx-input>
                            </div>

                            <div class="flex gap-2 pt-4">
                                <vx-button
                                    data-test="cancel"
                                    type="button"
                                    size="lg"
                                    color="muted-light"
                                    @click="cancel"
                                >
                                    Cancel
                                </vx-button>
                                <vx-button data-test="apply" size="lg" type="submit" block>
                                    Apply
                                    <font-awesome-icon widthAuto :icon="faCircleCheck"></font-awesome-icon>
                                </vx-button>
                            </div>
                        </form>
                    </template>
                </vx-filterable>
            </div>
            <div v-if="props.sortingEnabled" class="flex w-full gap-2 @xl/controls:w-auto">
                <vx-button-group size="lg" class="w-full">
                    <vx-select
                        v-model="filters.sort_field"
                        @update:modelValue="apply"
                        :options="[
                            {
                                value: 'name',
                                label: 'Sort by: Name',
                            },
                            {
                                value: 'id',
                                label: 'Sort by: Created',
                            },
                        ]"
                    ></vx-select>
                    <vx-button
                        data-test="sort-direction"
                        :aria-label="`Sort ${
                            props.modelValue.sort_dir === 'asc' ? 'ascending' : 'descending'
                        }. Press to toggle sort direction.`"
                        @click.prevent="
                            filters.sort_dir = filters.sort_dir === 'asc' ? 'desc' : 'asc';
                            apply();
                        "
                        type="button"
                        color="white"
                        shadow
                    >
                        <font-awesome-icon
                            widthAuto
                            :icon="props.modelValue.sort_dir === 'asc' ? faArrowUpWideShort : faArrowDownWideShort"
                        ></font-awesome-icon>
                    </vx-button>
                </vx-button-group>
            </div>
        </template>
        <template v-slot:right>
            <vx-button href="/kiosk/org-units/new">
                New OU <font-awesome-icon widthAuto :icon="faCirclePlus" size="lg" />
            </vx-button>
        </template>
    </page-controls>
</template>

<script setup>
import {
    faArrowDownWideShort,
    faArrowUpWideShort,
    faCircleCheck,
    faMagnifyingGlass,
    faCirclePlus,
    faCircleXmark,
} from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { VxBadge, VxButton, VxButtonGroup, VxFilterable, VxInput, VxSelect } from '@voxie/frontend-components';
import { computed, nextTick, ref, watch } from 'vue';
import PageControls from '~/components/general/PageControls.vue';
import FilterLabel from '~/components/general/FilterLabel.vue';
import { pluralized } from '~/components/filters';

const filters = ref({});
const props = defineProps({
    modelValue: {
        type: Object,
        required: true,
    },
    sortingEnabled: {
        type: Boolean,
        required: false,
        default: true,
    },
});

watch(
    () => props.modelValue,
    (modelValue) => {
        filters.value = { ...modelValue };
    },
    {
        deep: true,
        immediate: true,
    }
);

const emit = defineEmits(['update:modelValue', 'apply', 'reset']);

const active = ref(false);

const toggleFilterable = async (event) => {
    active.value = event;
};

const filtersCount = computed(() => (props.modelValue.query ? 1 : 0));

const reset = () => {
    active.value = false;
    emit('reset');
};

const cancel = async () => {
    filters.value = { ...props.modelValue };
    active.value = false;
};

const apply = async () => {
    active.value = false;
    emit('update:modelValue', { ...filters.value });
    await nextTick();
    emit('apply');
};
</script>
